import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert, CircularProgress, Avatar, Divider, LinearProgress, Tooltip,
  IconButton,
} from '@mui/material';
import {
  AccessTime, Add, CheckCircle, Cancel, HourglassEmpty,
  TrendingUp, Savings, RemoveCircleOutline, PendingActions,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  }).then(async r => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.message || 'Error'); return d; });
}

const STATUS_COLOR: Record<string, string> = {
  pending: '#f57f17', approved: '#2e7d32', rejected: '#c62828', cancelled: '#78909c',
};

interface ToilBalance { accrued: number; redeemed: number; pending: number; available: number; }
interface Redemption {
  id: string; startDate: string; endDate: string; hoursRequested: number;
  reason?: string; status: string; approverNote?: string; decidedAt?: string;
  employee?: { firstName: string; lastName: string };
  approver?: { firstName: string; lastName: string };
  createdAt: string;
}
interface Accrual {
  id: string; date: string; startTime: string; endTime: string;
  toilHours: number; overtimeType: string; reason: string; approvedAt: string;
}
interface StatsRow {
  employeeId: string; name: string; department: string;
  accrued: number; redeemed: number; pending: number; available: number;
}

const HOURS_OPTIONS = [1, 2, 4, 8];

export default function ToilBank() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes((user as any)?.role || '');
  const isManager = (user as any)?.role === 'manager';
  const canApprove = isHR || isManager;

  const [tab, setTab] = useState(0);
  const [balance, setBalance] = useState<ToilBalance | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [accruals, setAccruals] = useState<Accrual[]>([]);
  const [stats, setStats] = useState<{ pendingApprovals: number; balances: StatsRow[] } | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Request form
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState({ startDate: '', endDate: '', hoursRequested: 8, reason: '' });
  const [reqSaving, setReqSaving] = useState(false);

  // Approve/Reject
  const [actionDialog, setActionDialog] = useState<{ item: Redemption; type: 'approve' | 'reject' } | null>(null);
  const [actionNote, setActionNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const promises: Promise<any>[] = [
        apiFetch('/api/toil/balance/me'),
        apiFetch('/api/toil/redemptions'),
        apiFetch('/api/toil/accruals/me'),
      ];
      if (isHR) promises.push(apiFetch('/api/toil/stats'));

      const results = await Promise.allSettled(promises);
      if (results[0].status === 'fulfilled') setBalance(results[0].value);
      if (results[1].status === 'fulfilled') setRedemptions(results[1].value);
      if (results[2].status === 'fulfilled') setAccruals(results[2].value);
      if (results[3] && results[3].status === 'fulfilled') setStats((results[3] as any).value);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isHR]);

  useEffect(() => { load(); }, [load]);

  const submitRequest = async () => {
    setReqSaving(true);
    try {
      await apiFetch('/api/toil/redemptions', { method: 'POST', body: JSON.stringify(reqForm) });
      setReqOpen(false);
      load();
    } catch (e: any) { setError(e.message); }
    finally { setReqSaving(false); }
  };

  const handleAction = async () => {
    if (!actionDialog) return;
    const { item, type } = actionDialog;
    try {
      await apiFetch(`/api/toil/redemptions/${item.id}/${type}`, {
        method: 'PATCH', body: JSON.stringify({ note: actionNote }),
      });
      setActionDialog(null);
      setActionNote('');
      load();
    } catch (e: any) { setError(e.message); }
  };

  const cancelRedemption = async (id: string) => {
    await apiFetch(`/api/toil/redemptions/${id}/cancel`, { method: 'PATCH' });
    load();
  };

  const pending = redemptions.filter(r => r.status === 'pending');
  const allRedemptions = redemptions;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#4527a0', width: 48, height: 48 }}><AccessTime /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>TOIL Bank</Typography>
            <Typography variant="body2" color="text.secondary">Time-Off In Lieu — accrue and redeem overtime hours as leave</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setReqOpen(true)}
          sx={{ bgcolor: '#4527a0', '&:hover': { bgcolor: '#311b92' } }}>
          Redeem TOIL
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Balance Cards */}
      {balance && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Accrued', value: balance.accrued, icon: <TrendingUp />, color: '#4527a0', desc: 'Total TOIL earned from approved overtime' },
            { label: 'Redeemed', value: balance.redeemed, icon: <CheckCircle />, color: '#2e7d32', desc: 'Hours already taken as time off' },
            { label: 'Pending', value: balance.pending, icon: <PendingActions />, color: '#f57f17', desc: 'Awaiting approval' },
            { label: 'Available', value: balance.available, icon: <Savings />, color: '#1565c0', desc: 'Ready to use' },
          ].map(card => (
            <Grid size={{ xs: 6, sm: 3 }} key={card.label}>
              <Paper sx={{ p: 2, borderTop: `3px solid ${card.color}`, textAlign: 'center' }}>
                <Box sx={{ color: card.color, mb: 0.5 }}>{card.icon}</Box>
                <Typography variant="h4" fontWeight={700} color={card.color}>
                  {card.value.toFixed(1)}<Typography component="span" variant="caption" color="text.secondary"> hrs</Typography>
                </Typography>
                <Typography variant="subtitle2" fontWeight={600}>{card.label}</Typography>
                <Typography variant="caption" color="text.secondary">{card.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={`My Redemptions (${allRedemptions.length})`} />
            <Tab label={`How I Earned It (${accruals.length})`} />
            {canApprove && <Tab label={`Pending Approval${stats ? ` (${stats.pendingApprovals})` : ''}`} />}
            {isHR && <Tab label="All Balances" />}
          </Tabs>

          {/* My Redemptions */}
          {tab === 0 && (
            <>
              {pending.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You have {pending.length} pending TOIL redemption{pending.length > 1 ? 's' : ''} awaiting approval.
                </Alert>
              )}
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Period</TableCell>
                      <TableCell align="center">Hours</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Note</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allRedemptions.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No redemptions yet</TableCell></TableRow>
                    )}
                    {allRedemptions.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{r.startDate}</Typography>
                          {r.endDate !== r.startDate && <Typography variant="caption" color="text.secondary"> → {r.endDate}</Typography>}
                        </TableCell>
                        <TableCell align="center"><Chip size="small" label={`${r.hoursRequested}h`} color="primary" variant="outlined" /></TableCell>
                        <TableCell><Typography variant="body2">{r.reason || '—'}</Typography></TableCell>
                        <TableCell>
                          <Chip size="small" label={r.status}
                            sx={{ bgcolor: STATUS_COLOR[r.status] + '20', color: STATUS_COLOR[r.status], fontWeight: 600 }} />
                        </TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{r.approverNote || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption">{new Date(r.createdAt).toLocaleDateString()}</Typography></TableCell>
                        <TableCell>
                          {r.status === 'pending' && (
                            <Tooltip title="Cancel request">
                              <IconButton size="small" color="error" onClick={() => cancelRedemption(r.id)}>
                                <Cancel fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Accrual History */}
          {tab === 1 && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Hours Worked</TableCell>
                    <TableCell>TOIL Earned</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Approved</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accruals.length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No TOIL accruals yet. Submit overtime requests with "Time Off" compensation type.
                    </TableCell></TableRow>
                  )}
                  {accruals.map(a => (
                    <TableRow key={a.id} hover>
                      <TableCell>{a.date}</TableCell>
                      <TableCell><Typography variant="body2">{a.startTime} – {a.endTime}</Typography></TableCell>
                      <TableCell>
                        <Chip size="small" icon={<AccessTime fontSize="small" />}
                          label={`+${parseFloat(a.toilHours as any).toFixed(1)}h`}
                          sx={{ bgcolor: '#4527a020', color: '#4527a0', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={a.overtimeType} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                      </TableCell>
                      <TableCell><Typography variant="body2">{a.reason}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{a.approvedAt ? new Date(a.approvedAt).toLocaleDateString() : '—'}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Pending Approval (manager / HR) */}
          {canApprove && tab === 2 && (
            <ToilPendingTab canApprove={canApprove} onAction={(item, type) => { setActionDialog({ item, type }); setActionNote(''); }} />
          )}

          {/* All Balances (HR) */}
          {isHR && tab === 3 && stats && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department</TableCell>
                    <TableCell align="right">Accrued (h)</TableCell>
                    <TableCell align="right">Redeemed (h)</TableCell>
                    <TableCell align="right">Pending (h)</TableCell>
                    <TableCell align="right">Available (h)</TableCell>
                    <TableCell>Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.balances.map(row => (
                    <TableRow key={row.employeeId} hover>
                      <TableCell><Typography variant="body2" fontWeight={500}>{row.name}</Typography></TableCell>
                      <TableCell><Typography variant="caption">{row.department || '—'}</Typography></TableCell>
                      <TableCell align="right">{row.accrued.toFixed(1)}</TableCell>
                      <TableCell align="right">{row.redeemed.toFixed(1)}</TableCell>
                      <TableCell align="right">{row.pending.toFixed(1)}</TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color={row.available > 0 ? '#2e7d32' : '#c62828'}>
                          {row.available.toFixed(1)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 120 }}>
                        <LinearProgress
                          variant="determinate"
                          value={row.accrued > 0 ? Math.min(100, (row.redeemed / row.accrued) * 100) : 0}
                          sx={{ height: 6, borderRadius: 3,
                            bgcolor: '#e8f5e9',
                            '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' } }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Redeem Request Dialog */}
      <Dialog open={reqOpen} onClose={() => setReqOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Redeem TOIL Hours</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {balance && (
            <Alert severity={balance.available > 0 ? 'success' : 'warning'}>
              Available balance: <strong>{balance.available.toFixed(1)} hours</strong>
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField fullWidth label="Start Date" type="date" InputLabelProps={{ shrink: true }}
              value={reqForm.startDate} onChange={e => setReqForm(p => ({ ...p, startDate: e.target.value }))} />
            <TextField fullWidth label="End Date" type="date" InputLabelProps={{ shrink: true }}
              value={reqForm.endDate} onChange={e => setReqForm(p => ({ ...p, endDate: e.target.value }))} />
          </Box>
          <TextField
            fullWidth label="Hours to Redeem" type="number"
            value={reqForm.hoursRequested}
            onChange={e => setReqForm(p => ({ ...p, hoursRequested: Number(e.target.value) }))}
            inputProps={{ min: 0.5, step: 0.5 }}
            helperText={`Max available: ${balance?.available.toFixed(1) ?? '—'}h`}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {HOURS_OPTIONS.map(h => (
              <Chip key={h} label={`${h}h`} size="small" clickable
                variant={reqForm.hoursRequested === h ? 'filled' : 'outlined'}
                color={reqForm.hoursRequested === h ? 'primary' : 'default'}
                onClick={() => setReqForm(p => ({ ...p, hoursRequested: h }))} />
            ))}
          </Box>
          <TextField fullWidth label="Reason (optional)" multiline rows={2}
            value={reqForm.reason} onChange={e => setReqForm(p => ({ ...p, reason: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReqOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitRequest} disabled={reqSaving || !reqForm.startDate || reqForm.hoursRequested <= 0}
            sx={{ bgcolor: '#4527a0' }}>
            {reqSaving ? 'Submitting…' : 'Submit Request'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve / Reject Dialog */}
      <Dialog open={!!actionDialog} onClose={() => setActionDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{actionDialog?.type === 'approve' ? '✅ Approve' : '❌ Reject'} TOIL Redemption</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {actionDialog && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{actionDialog.item.employee?.firstName} {actionDialog.item.employee?.lastName}</strong> · {actionDialog.item.hoursRequested}h
              </Typography>
              <Typography variant="caption" color="text.secondary">{actionDialog.item.startDate} → {actionDialog.item.endDate}</Typography>
            </Box>
          )}
          <TextField fullWidth label={actionDialog?.type === 'reject' ? 'Reason (required)' : 'Note (optional)'}
            value={actionNote} onChange={e => setActionNote(e.target.value)} multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialog(null)}>Cancel</Button>
          <Button
            variant="contained" onClick={handleAction}
            disabled={actionDialog?.type === 'reject' && !actionNote}
            color={actionDialog?.type === 'approve' ? 'success' : 'error'}
          >
            {actionDialog?.type === 'approve' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Sub-component: pending redemptions tab for managers/HR */
function ToilPendingTab({ canApprove, onAction }: { canApprove: boolean; onAction: (item: Redemption, type: 'approve' | 'reject') => void }) {
  const [items, setItems] = useState<Redemption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/toil/redemptions?status=pending')
      .then(d => setItems(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress />;

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Employee</TableCell>
            <TableCell>Period</TableCell>
            <TableCell align="center">Hours</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Submitted</TableCell>
            {canApprove && <TableCell align="center">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 && (
            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No pending redemptions</TableCell></TableRow>
          )}
          {items.map(r => (
            <TableRow key={r.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {r.employee?.firstName} {r.employee?.lastName}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{r.startDate}</Typography>
                {r.endDate !== r.startDate && <Typography variant="caption" color="text.secondary"> → {r.endDate}</Typography>}
              </TableCell>
              <TableCell align="center">
                <Chip size="small" label={`${r.hoursRequested}h`} color="warning" />
              </TableCell>
              <TableCell><Typography variant="body2">{r.reason || '—'}</Typography></TableCell>
              <TableCell><Typography variant="caption">{new Date(r.createdAt).toLocaleDateString()}</Typography></TableCell>
              {canApprove && (
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Approve">
                      <IconButton size="small" color="success" onClick={() => onAction(r, 'approve')}>
                        <CheckCircle fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton size="small" color="error" onClick={() => onAction(r, 'reject')}>
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
